import styled from 'styled-components';
import { Button, Steps, DatePicker, Input, Select, Typography, Checkbox, Radio, Divider, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

// Calming color palette
export const colors = {
  primary: '#4A90E2',      // Calming blue
  secondary: '#7FB3D5',    // Soft blue
  accent: '#D4E6F1',       // Very light blue
  text: '#2C3E50',         // Dark blue-gray
  background: '#F5F9FC',   // Off-white with blue hint
  success: '#5cb85c',      // Gentle green
  error: '#d9534f',        // Soft red
  warning: '#f0ad4e',      // Warm amber
  white: '#FFFFFF',
  gray: '#ECEFF1'
};

// Styled components with improved mobile responsiveness
export const WizardContainer = styled.div`
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  padding: 32px 24px 24px 24px;
  background: ${colors.white};
  border-radius: 12px;
  
  @media (max-width: 768px) {
    padding: 24px 16px 16px 16px;
    border-radius: 8px;
    margin-top: 8px;
    margin-bottom: 8px;
  }
`;

export const WizardHeader = styled.div`
  margin-bottom: 24px;
  text-align: center;
  padding-bottom: 8px;
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
`;

export const StepContainer = styled.div`
  margin-top: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    margin-top: 20px;
    margin-bottom: 24px;
  }
`;

export const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
  
  @media (max-width: 768px) {
    flex-direction: ${props => props.isLastStep ? 'column-reverse' : 'row'};
    
    button {
      margin-top: ${props => props.isLastStep ? '10px' : '0'};
      width: ${props => props.isLastStep ? '100%' : 'auto'};
    }
  }
`;

export const FormItem = styled.div`
  margin-bottom: 36px;
  
  &:last-child {
    margin-bottom: 16px;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 32px;
    
    &:last-child {
      margin-bottom: 14px;
    }
  }
`;

export const ErrorText = styled.div`
  color: ${colors.error};
  font-size: 14px;
  margin-top: 8px;
  margin-bottom: 4px;
  min-height: 20px;
  font-weight: 400;
`;

export const StepTitle = styled(Title)`
  margin-bottom: 16px !important;
  color: ${colors.text} !important;
  font-weight: 600 !important;
  font-size: 24px !important;
  line-height: 1.3 !important;
  
  @media (max-width: 768px) {
    font-size: 22px !important;
    margin-bottom: 14px !important;
  }
`;

export const SectionTitle = styled(Text)`
  font-weight: 600 !important;
  color: ${colors.text} !important;
  font-size: 16px !important;
  line-height: 1.4 !important;
  display: block !important;
  margin-bottom: 8px !important;
`;

export const StyledSteps = styled(Steps)`
  display: none; /* Hide on desktop */
  
  .ant-steps-item-process .ant-steps-item-icon {
    background-color: ${colors.primary};
    border-color: ${colors.primary};
  }
  
  .ant-steps-item-finish .ant-steps-item-icon {
    border-color: ${colors.primary};
  }
  
  .ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon {
    color: ${colors.primary};
  }
  
  .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-content > .ant-steps-item-title::after {
    background-color: ${colors.primary};
  }
  
  @media (max-width: 768px) {
    display: none !important; /* Force hide with !important */
    visibility: hidden;
    height: 0;
    overflow: hidden;
    margin: 0;
    padding: 0;
  }
`;

export const StyledButton = styled(Button)`
  border-radius: 25px;
  height: 45px;
  font-weight: 600;
  font-size: 16px;
  padding: 0 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &.ant-btn-primary {
    background-color: ${colors.primary};
    border-color: ${colors.primary};
    
    &:hover, &:focus {
      background-color: ${colors.secondary};
      border-color: ${colors.secondary};
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(74, 144, 226, 0.3);
    }
  }
  
  &.ant-btn-default {
    background-color: ${colors.white};
    border-color: ${colors.primary};
    color: ${colors.primary};
    
    &:hover, &:focus {
      background-color: ${colors.accent};
      border-color: ${colors.primary};
      color: ${colors.primary};
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(74, 144, 226, 0.2);
    }
  }
`;

export const InfoBox = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  
  .icon {
    color: ${colors.primary};
    font-size: 20px;
    margin-right: 12px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
  
  .content {
    flex: 1;
    
    p {
      margin: 0 !important;
      font-size: 15px !important;
      line-height: 1.6 !important;
      color: ${colors.text} !important;
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 18px;
    
    .icon {
      font-size: 18px;
      margin-right: 10px;
    }
    
    .content p {
      font-size: 14px !important;
    }
  }
`;

export const FormProgress = styled.div`
  display: none; /* Hide on desktop */
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 20px;
    padding: 12px 16px;
    background: ${colors.accent};
    border-radius: 8px;
    border-left: 4px solid ${colors.primary};
    
    .title {
      font-size: 14px;
      color: ${colors.text};
      font-weight: 500;
    }
    
    .steps {
      margin-top: 4px;
      font-size: 16px;
      color: ${colors.primary};
      font-weight: 600;
      line-height: 1.3;
    }
  }
`;

// Add custom sizes for inputs and form controls
export const LargeInput = styled(Input)`
  height: 48px;
  font-size: 16px;
  padding: 8px 16px;
`;

export const LargeSelect = styled(Select)`
  .ant-select-selector {
    height: 48px !important;
    padding: 8px 16px !important;
    
    .ant-select-selection-item {
      line-height: 32px !important;
      font-size: 16px;
      color: ${colors.text} !important;
    }
    
    .ant-select-selection-placeholder {
      color: #999 !important;
      font-size: 16px;
    }
  }
  
  .ant-select-dropdown {
    .ant-select-item {
      color: ${colors.text} !important;
      background-color: white !important;
      
      &:hover {
        background-color: ${colors.accent} !important;
        color: ${colors.text} !important;
      }
      
      &.ant-select-item-option-selected {
        background-color: ${colors.primary} !important;
        color: white !important;
        
        &:hover {
          background-color: ${colors.secondary} !important;
          color: white !important;
        }
      }
    }
  }
  
  &.ant-select-open .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    border-color: ${colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2) !important;
  }
`;

export const LargeDatePicker = styled(DatePicker)`
  height: 48px;
  font-size: 16px;
  
  input {
    font-size: 16px;
    padding: 8px 16px;
    color: ${colors.text} !important;
    background: white !important;
    
    &::placeholder {
      color: #999 !important;
    }
  }
  
  .ant-picker-suffix {
    color: ${colors.text} !important;
  }
  
  &.ant-picker-focused {
    border-color: ${colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2) !important;
  }
`;

export const LargeCheckbox = styled(Checkbox)`
  .ant-checkbox {
    transform: scale(1.3);
    margin-right: 4px;
  }
  
  .ant-checkbox-inner {
    width: 20px;
    height: 20px;
    border-width: 2px;
  }
  
  .ant-checkbox-checked .ant-checkbox-inner::after {
    transform: rotate(45deg) scale(1) translate(-50%, -60%);
  }
  
  .ant-checkbox + span {
    font-size: 16px;
    padding-left: 12px;
    padding-right: 8px;
    line-height: 1.6;
  }
  
  &:hover .ant-checkbox-inner {
    border-color: ${colors.primary};
  }
`;

// Variant of LargeCheckbox that aligns the checkbox to the top of the
// (potentially multi-line) label text instead of vertically centering it.
export const TopAlignedCheckbox = styled(LargeCheckbox)`
  align-items: flex-start;

  .ant-checkbox {
    align-self: flex-start;
    top: 0;
    margin-top: 2px;
  }
`;

export const LargeRadio = styled(Radio)`
  .ant-radio {
    top: 0;
  }
  
  .ant-radio-inner {
    width: 20px;
    height: 20px;
    border-width: 2px;
  }
  
  .ant-radio-inner::after {
    width: 10px;
    height: 10px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(1);
    margin: 0;
  }
  
  .ant-radio-checked .ant-radio-inner::after {
    transform: translate(-50%, -50%) scale(1);
  }
  
  .ant-radio + span {
    font-size: 16px;
    padding-right: 8px;
    padding-left: 12px;
    line-height: 1.6;
  }
  
  &:hover .ant-radio-inner {
    border-color: ${colors.primary};
  }
`;

export const FormSection = styled.div`
  background: ${colors.background};
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  border: 2px solid ${colors.accent};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12), 0 3px 6px rgba(0, 0, 0, 0.06);
  }
  
  @media (max-width: 768px) {
    padding: 18px 20px;
    margin-bottom: 18px;
    border-width: 1px;
    border-radius: 10px;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
    
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
    }
  }
`;

// Global styles for calendar popup in BusDataFormWizard
export const GlobalSelectStyles = styled.div`
  /* Fix for date picker calendar popup text visibility */
  .ant-picker-dropdown {
    .ant-picker-panel {
      background: white !important;
      
      .ant-picker-header {
        border-bottom: 1px solid #f0f0f0;
        
        button {
          color: ${colors.text} !important;
          
          &:hover {
            color: ${colors.primary} !important;
          }
        }
      }
      
      .ant-picker-content {
        th {
          color: ${colors.text} !important;
        }
        
        td {
          .ant-picker-cell-inner {
            color: ${colors.text} !important;
            background: white !important;
            
            &:hover {
              background-color: rgba(74, 144, 226, 0.1) !important;
              color: ${colors.text} !important;
            }
          }
          
          &.ant-picker-cell-selected .ant-picker-cell-inner {
            background-color: ${colors.primary} !important;
            color: white !important;
          }
          
          &.ant-picker-cell-today .ant-picker-cell-inner {
            border-color: ${colors.primary} !important;
            color: ${colors.text} !important;
          }
          
          &.ant-picker-cell-disabled .ant-picker-cell-inner {
            color: #d9d9d9 !important;
            background: transparent !important;
          }
        }
      }
    }
  }
`;

// Inline style components for specific use cases
export const inlineStyles = {
  // Paragraphs
  infoParagraph: { 
    margin: '0', 
    color: colors.text, 
    fontSize: '15px', 
    lineHeight: '1.5',
    fontWeight: '400'
  },
  instructionParagraph: { 
    fontSize: '15px', 
    color: colors.text, 
    marginTop: '6px', 
    marginBottom: '12px',
    lineHeight: '1.4',
    fontWeight: '400'
  },
  detailParagraph: { 
    fontSize: '14px', 
    color: colors.text, 
    marginTop: '6px', 
    marginBottom: '14px',
    lineHeight: '1.5',
    fontWeight: '400'
  },
  smallDetailParagraph: { 
    fontSize: '14px', 
    color: colors.text, 
    marginTop: '6px', 
    marginBottom: '10px',
    lineHeight: '1.4',
    fontWeight: '400'
  },
  submitParagraph: { 
    fontSize: '16px', 
    color: colors.text, 
    marginBottom: '24px',
    lineHeight: '1.5',
    fontWeight: '400',
    textAlign: 'center'
  },
    // Divs and containers
  topMarginDiv: { marginTop: '14px' },
  centerLoadingDiv: { marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  successContainer: { 
    minHeight: '100vh', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    padding: '20px'
  },
  successInnerContainer: { textAlign: 'center', padding: '40px 20px' },
  errorContainer: { textAlign: 'center', padding: '40px 20px' },
    // Form inputs
  errorBorderInput: (touched, errors) => ({ 
    borderColor: touched && errors ? colors.error : null 
  }),
  datePickerStyle: (touched, errors) => ({
    width: '100%',
    borderColor: touched && errors ? colors.error : null
  }),
  selectStyle: (touched, errors) => ({
    width: '100%',
    borderColor: touched && errors ? colors.error : null
  }),  // Texts and titles
  errorTitle: { color: colors.error, marginBottom: '16px' },
  dividerText: { color: colors.text, fontSize: '14px' },
  loadingSpan: { marginLeft: '12px', color: colors.text },
  
  // Alerts
  alertStyle: { marginTop: '16px' },
  
  // Buttons
  submitButton: { 
    backgroundColor: '#000', 
    borderColor: '#000',
    height: '48px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    justifySelf: 'anchor-center',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'none'
  },
  primaryButton: {
    height: '48px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'none',
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    marginRight: '12px'
  },
  defaultButton: {
    height: '48px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'none'
  },
  
  // Checkboxes and other form elements
  checkboxStyle: (touched, errors) => ({
    lineHeight: '1.5',
    color: touched && errors ? colors.error : colors.text
  }),
  
  // Special containers
  waiverContainer: { borderRadius: '8px', marginBottom: '16px' },
  listStyle: { marginTop: '8px', paddingLeft: '20px' },
    // Dividers
  dividerStyle: { margin: '24px 0 16px' },
  waiverDivider: { margin: '28px 0 20px' },
  finalDivider: { margin: '32px 0 24px' },
  headerDivider: { 
    margin: '16px 0 0 0', 
    borderColor: '#d9d9d9',
    borderWidth: '1px',
    borderStyle: 'solid'
  },
  
  // Spacing
  spaceVertical: { width: '100%' },
  
  // Icons
  loadingIcon: { fontSize: 24, color: colors.primary },
  heartIcon: { 
    fontSize: '48px', 
    color: '#1a1a1a', 
    marginBottom: '24px',
    display: 'block',
    animation: 'heartbeat 1.5s infinite'
  },
    // Date picker specific
  currentDatePicker: (touched, errors) => ({
    width: '100%',
    borderColor: touched && errors ? colors.error : null
  })
};

// Custom components for specific inline elements
export const LoadingSpinner = styled(Spin).attrs({
  indicator: <LoadingOutlined style={inlineStyles.loadingIcon} spin />
})``;

export const HeartbeatIcon = styled.div`
  font-size: 48px;
  color: #1a1a1a;
  margin-bottom: 24px;
  display: block;
  animation: heartbeat 1.5s infinite;
  
  @keyframes heartbeat {
    0% { 
      transform: scale(1);
      opacity: 1;
    }
    50% { 
      transform: scale(1.2);
      opacity: 0.8;
    }
    100% { 
      transform: scale(1);
      opacity: 1;
    }
  }
`;

export const MainTitle = styled(Title)`
  color: ${colors.text} !important;
  margin-bottom: 0 !important;
  text-align: center !important;
  font-size: 32px !important;
  font-weight: 700 !important;
  line-height: 1.2 !important;
  letter-spacing: -0.5px !important;
  
  @media (max-width: 768px) {
    font-size: 28px !important;
    letter-spacing: -0.3px !important;
  }
`;

export const StyledDivider = styled(Divider)`
  margin: ${props => props.spacing || '24px 0 16px'};
  border-color: ${props => props.borderColor || 'initial'};
`;

export const StyledText = styled(Text)`
  color: ${props => props.textColor || colors.text};
  font-size: ${props => props.fontSize || '14px'};
`;

export const StyledParagraph = styled(Paragraph)`
  margin: ${props => props.margin || '0'};
  color: ${props => props.textColor || colors.text};
  font-size: ${props => props.fontSize || '16px'};
  margin-top: ${props => props.marginTop || '0'};
  margin-bottom: ${props => props.marginBottom || '0'};
`;

// Global styles for responsive home button
export const GlobalButtonStyles = `
  /* Heartbeat animation for inline use */
  @keyframes heartbeat {
    0% { 
      transform: scale(1);
      opacity: 1;
    }
    50% { 
      transform: scale(1.2);
      opacity: 0.8;
    }
    100% { 
      transform: scale(1);
      opacity: 1;
    }
  }
  
  .responsive-home-button {
    width: auto !important;
    min-width: 120px !important;
  }
  
  @media (max-width: 768px) {
    .responsive-home-button {
      width: 100% !important;
      min-width: unset !important;
    }
  }
  
  /* Custom react-datepicker styling */
  .date-picker-wrapper {
    width: 100%;
  }
  
  .react-datepicker-wrapper {
    width: 100%;
    display: block;
  }
  
  .react-datepicker__input-container {
    width: 100%;
    display: block;
  }
  
  .react-datepicker__input-container input {
    width: 100%;
    height: 48px;
    padding: 8px 16px;
    font-size: 16px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    transition: all 0.3s;
    color: ${colors.text};
    background: white;
    box-sizing: border-box;
    font-family: inherit;
  }
  
  .react-datepicker__input-container input:hover {
    border-color: ${colors.primary};
  }
  
  .react-datepicker__input-container input:focus {
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
    outline: none;
  }
  
  .react-datepicker__input-container input.error-datepicker {
    border-color: ${colors.error};
  }
  
  .react-datepicker__input-container input::placeholder {
    color: #999;
    font-size: 16px;
  }
  
  .react-datepicker {
    font-family: inherit;
    border: 1px solid ${colors.primary};
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    font-size: 14px;
  }
  
  .react-datepicker__header {
    background-color: ${colors.primary};
    border-bottom: none;
    border-radius: 8px 8px 0 0;
    padding-top: 12px;
    padding-bottom: 10px;
  }
  
  .react-datepicker__current-month,
  .react-datepicker__day-name {
    color: white;
    font-weight: 600;
  }
  
  .react-datepicker__current-month {
    font-size: 15px;
    margin-bottom: 8px;
  }
  
  .react-datepicker__day-name {
    font-size: 13px;
  }
  
  .react-datepicker__day {
    color: ${colors.text};
    border-radius: 4px;
    transition: all 0.2s;
    width: 2rem;
    line-height: 2rem;
    font-size: 13px;
  }
  
  .react-datepicker__day:hover {
    background-color: ${colors.accent};
    color: ${colors.primary};
  }
  
  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: ${colors.primary};
    color: white;
    font-weight: 600;
  }
  
  .react-datepicker__day--selected:hover {
    background-color: ${colors.secondary};
  }
  
  .react-datepicker__day--today {
    font-weight: 600;
    color: ${colors.primary};
    border: 1px solid ${colors.primary};
    background-color: ${colors.accent};
  }
  
  .react-datepicker__day--today.react-datepicker__day--selected {
    color: white;
    background-color: ${colors.primary};
  }
  
  .react-datepicker__day--disabled {
    color: #ccc;
    cursor: not-allowed;
  }
  
  .react-datepicker__day--disabled:hover {
    background-color: transparent;
  }
  
  .react-datepicker__navigation {
    top: 13px;
    width: 30px;
    height: 30px;
  }
  
  .react-datepicker__navigation-icon::before {
    border-color: white;
    border-width: 2px 2px 0 0;
    height: 7px;
    width: 7px;
  }
  
  .react-datepicker__navigation:hover *::before {
    border-color: ${colors.accent};
  }
  
  .react-datepicker__month {
    margin: 0.8rem;
  }
  
  .react-datepicker__month-dropdown-container,
  .react-datepicker__year-dropdown-container {
    margin: 0 5px;
  }
  
  .react-datepicker__month-dropdown,
  .react-datepicker__year-dropdown {
    background-color: white;
    border: 1px solid ${colors.primary};
    border-radius: 4px;
    padding: 4px;
    font-size: 13px;
  }
  
  .react-datepicker__month-read-view,
  .react-datepicker__year-read-view {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 14px;
  }
  
  .react-datepicker__month-read-view:hover,
  .react-datepicker__year-read-view:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  .react-datepicker__year-option,
  .react-datepicker__month-option {
    padding: 6px 10px;
    font-size: 13px;
  }
  
  .react-datepicker__year-option:hover,
  .react-datepicker__month-option:hover {
    background-color: ${colors.accent};
  }
  
  .react-datepicker__year-option--selected,
  .react-datepicker__month-option--selected {
    background-color: ${colors.primary};
    color: white;
  }
  
  .react-datepicker__year-dropdown,
  .react-datepicker__month-dropdown {
    max-height: 200px;
    overflow-y: auto;
  }
  
  @media (max-width: 768px) {
    .react-datepicker__input-container input {
      font-size: 16px; /* Prevents zoom on iOS */
    }
    
    .react-datepicker {
      font-size: 14px;
    }
  }
`;
